import { TooltipDelay } from '@fluentui/react';
import type { IOutOfOfficePreferredMethods } from 'shared-state/identity/atoms';
import { AtWorkLocation, IProfile, UserStatus } from 'shared-state/identity/types';
import {
    useCanUseStatus,
    useLocalProfile,
    useAtWorkLocation,
    useAtWorkCustomPhrase,
    useAwayStatusMessage,
    useFocusEndTime,
    useFocusDurationMinutes,
    useUserStatus,
    useOutOfOfficeReturnDate,
    useOutOfOfficeReturnTime,
    useOutOfOfficeAbsenceComment,
    useOutOfOfficePreferredMethods,
    useOtherUsersStatus
} from 'shared-state/identity/hooks';
import { IUserFaceActionStyles } from './UserFace.view';
import { useUserActions } from '../helpers/userHooks/userHooks';
import { useStatusActions } from '../helpers/userHooks/statusActions';
import { IUserAction, IStatusAction } from '../helpers/userHooks/userTypes';
import { IStoredStatus } from 'shared-state/identity/statusPersistence';
import { useUsersInRoom } from 'shared-state/presence/hooks';

export interface IUserFaceState {
    profileImageURL: string | undefined;
    actions: IUserAction[];
    statusActions: IStatusAction[];
    toolTipProps: {
        delay: TooltipDelay;
    };
    actionsShouldBeDisplayOnHover: boolean;
    detailsAreHidden: boolean;
    isCurrentUser: boolean;
    canUseStatus: boolean;
    currentStatusColor: string | undefined;
    awayStatusMessage: string | undefined;
    atWorkLocation: AtWorkLocation;
    atWorkCustomPhrase: string | undefined;
    currentStatus: string | undefined;
    focusEndTime: number | undefined;
    setFocusEndTime: (timestamp: number | undefined) => void;
    focusDurationMinutes: number | undefined;
    setFocusDurationMinutes: (minutes: number | undefined) => void;
    setCurrentStatus: (status: UserStatus) => void;
    outOfOfficeReturnDate: Date | undefined;
    outOfOfficeReturnTime: string | null | undefined;
    outOfOfficeAbsenceComment: string;
    outOfOfficePreferredMethods: IOutOfOfficePreferredMethods;
    otherUsersStatus: IStoredStatus | null;
    isUserAloneInRoom: boolean;
    isUserInMeetingInPersonalRoom: boolean;
    isUserInMeetingInPublicRoom: boolean;
}

export const useUserFaceState: (user: IProfile, showDetails: boolean | undefined, actionStyles: IUserFaceActionStyles | undefined) => IUserFaceState = (
    user,
    showDetails,
    actionStyles
) => {
    const profileImageURL = user.profileImage;
    const localProfile = useLocalProfile();

    // Check if the user being displayed is the current user
    const isCurrentUser = localProfile?.uid === user.uid;

    // Get actions for other users
    const actions: IUserAction[] = useUserActions(user);

    // Get status actions for current user (includes current status color)
    const { actions: allStatusActions, currentStatusColor, currentStatus } = useStatusActions();
    // visitors have no status feature
    const canUseStatus = useCanUseStatus();
    const statusActions = canUseStatus ? allStatusActions : [];

    // Get away status message for current user
    const [awayStatusMessage] = useAwayStatusMessage();

    const [atWorkLocation] = useAtWorkLocation();
    const [atWorkCustomPhrase] = useAtWorkCustomPhrase();

    const [focusEndTime, setFocusEndTime] = useFocusEndTime();
    const [focusDurationMinutes, setFocusDurationMinutes] = useFocusDurationMinutes();
    const [, setCurrentStatus] = useUserStatus();
    const [outOfOfficeReturnDate] = useOutOfOfficeReturnDate();
    const [outOfOfficeReturnTime] = useOutOfOfficeReturnTime();
    const [outOfOfficeAbsenceComment] = useOutOfOfficeAbsenceComment();
    const [outOfOfficePreferredMethods] = useOutOfOfficePreferredMethods();
    const otherUsersStatus = useOtherUsersStatus(user.uid);
    const usersInRoom = useUsersInRoom(user.room || '');
    const isUserAloneInRoom = usersInRoom.length === 1;
    const isUserInMeetingInPersonalRoom = (!isUserAloneInRoom && user?.room?.includes('personal-')) || false;
    const isUserInMeetingInPublicRoom = (!isUserAloneInRoom && !user?.room?.includes('personal-')) || false;
    const toolTipProps = {
        delay: TooltipDelay.zero
    };

    // Show hover actions if there are actions available (either status or other user actions)
    const hasStatusActions = isCurrentUser && statusActions?.length > 0;
    const hasOtherActions = !isCurrentUser && actions?.length > 0;
    const actionsShouldBeDisplayOnHover = (hasStatusActions || hasOtherActions) && !actionStyles?.hidden && !actionStyles?.expanded;

    const detailsAreHidden = !showDetails;

    return {
        profileImageURL,
        actions,
        statusActions,
        toolTipProps,
        actionsShouldBeDisplayOnHover,
        detailsAreHidden,
        isCurrentUser,
        canUseStatus,
        currentStatusColor,
        awayStatusMessage,
        atWorkLocation,
        atWorkCustomPhrase,
        currentStatus,
        focusEndTime,
        setFocusEndTime,
        focusDurationMinutes,
        setFocusDurationMinutes,
        setCurrentStatus,
        outOfOfficeReturnDate,
        outOfOfficeReturnTime,
        outOfOfficeAbsenceComment,
        outOfOfficePreferredMethods,
        otherUsersStatus,
        isUserAloneInRoom,
        isUserInMeetingInPersonalRoom,
        isUserInMeetingInPublicRoom
    };
};
